import React, { useEffect, useState } from 'react';
import { API } from '../api';
import { defaultProductPicture } from '../imageUtils';

function toDateInputValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function defaultStartDate() {
  const now = new Date();
  return toDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1));
}

function formatQuantity(value) {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 3 });
}

export default function ProductData({ token, user }) {
  const [stats, setStats] = useState({ overall: [], details: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [productQuery, setProductQuery] = useState('');
  const [product, setProduct] = useState(null);
  const [productResults, setProductResults] = useState([]);

  const [farmerQuery, setFarmerQuery] = useState('');
  const [farmer, setFarmer] = useState(null);
  const [farmerResults, setFarmerResults] = useState([]);

  const [buyerQuery, setBuyerQuery] = useState('');
  const [buyer, setBuyer] = useState(null);
  const [buyerResults, setBuyerResults] = useState([]);

  const [start, setStart] = useState(defaultStartDate());
  const [end, setEnd] = useState(toDateInputValue(new Date()));

  const canFilterFarmer = user?.role === 'buyer' || user?.role === 'admin';
  const canFilterBuyer = user?.role === 'farmer' || user?.role === 'admin';

  useEffect(() => {
    const t = setTimeout(() => {
      if (!token || !productQuery || product?.name === productQuery) {
        setProductResults([]);
        return;
      }
      fetch(`${API}/products/search?q=${encodeURIComponent(productQuery)}`, { headers: { Authorization: token } })
        .then((res) => res.json())
        .then(setProductResults)
        .catch(() => setProductResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [productQuery, product, token]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!token || !farmerQuery || farmer?.name === farmerQuery) {
        setFarmerResults([]);
        return;
      }
      fetch(`${API}/farmers/search?q=${encodeURIComponent(farmerQuery)}`, { headers: { Authorization: token } })
        .then((res) => res.json())
        .then(setFarmerResults)
        .catch(() => setFarmerResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [farmerQuery, farmer, token]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!token || !buyerQuery || buyer?.name === buyerQuery) {
        setBuyerResults([]);
        return;
      }
      fetch(`${API}/buyers/search?q=${encodeURIComponent(buyerQuery)}`, { headers: { Authorization: token } })
        .then((res) => res.json())
        .then(setBuyerResults)
        .catch(() => setBuyerResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [buyerQuery, buyer, token]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    const params = new URLSearchParams();
    if (product) params.set('product_id', product.id);
    if (farmer) params.set('farmer_id', farmer.id);
    if (buyer) params.set('buyer_id', buyer.id);
    if (start) params.set('start', start);
    if (end) params.set('end', end);

    setLoading(true);
    setError('');
    fetch(`${API}/product-stats?${params.toString()}`, { headers: { Authorization: token } })
      .then(async (res) => {
        if (!res.ok) throw new Error(await res.text());
        return res.json();
      })
      .then(setStats)
      .catch(() => setError('Could not load product data. Make sure the backend is running.'))
      .finally(() => setLoading(false));
  }, [token, product, farmer, buyer, start, end]);

  const clearAll = () => {
    setProduct(null);
    setProductQuery('');
    setFarmer(null);
    setFarmerQuery('');
    setBuyer(null);
    setBuyerQuery('');
  };

  const renderProductFilter = () => (
    <div className="filterField">
      <label>Product</label>
      <input
        placeholder="Search product"
        value={productQuery}
        onChange={(e) => {
          setProductQuery(e.target.value);
          if (product && e.target.value !== product.name) setProduct(null);
        }}
      />
      {productResults.length > 0 && (
        <div className="filterResults">
          {productResults.map((item) => (
            <button className="filterResult" key={item.id} onClick={() => {
              setProduct(item);
              setProductQuery(item.name);
              setProductResults([]);
            }}>
              <img className="productThumb" src={item.picture || defaultProductPicture} alt="" />
              <span>{item.name}</span>
              {item.reference_number && <small>{item.reference_number}</small>}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderUserFilter = (label, query, setQuery, selected, setSelected, results, setResults) => (
    <div className="filterField">
      <label>{label}</label>
      <input
        placeholder={`Search ${label.toLowerCase()}`}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (selected && e.target.value !== selected.name) setSelected(null);
        }}
      />
      {results.length > 0 && (
        <div className="filterResults">
          {results.map((item) => (
            <button className="filterResult userFilterResult" key={item.id} onClick={() => {
              setSelected(item);
              setQuery(item.name);
              setResults([]);
            }}>
              <span>{item.name}</span>
              {(item.registration_number || item.username) && <small>{item.registration_number || item.username}</small>}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="wideContainer">
      <div className="pageHeader">
        <div>
          <h2>Product Data</h2>
          <p>Totals by product for the selected period.</p>
        </div>
      </div>

      <section className="filterPanel statsFilterPanel">
        {renderProductFilter()}
        {canFilterFarmer && renderUserFilter('Farmer', farmerQuery, setFarmerQuery, farmer, setFarmer, farmerResults, setFarmerResults)}
        {canFilterBuyer && renderUserFilter('Buyer', buyerQuery, setBuyerQuery, buyer, setBuyer, buyerResults, setBuyerResults)}
        <div className="filterField">
          <label>Start date</label>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        </div>
        <div className="filterField">
          <label>End date</label>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
        <button className="secondaryButton filterButton" onClick={clearAll}>Clear Filters</button>
      </section>

      {loading && <p>Loading...</p>}
      {error && <p className="error">{error}</p>}

      {!loading && !error && (
        <>
          <section className="statsSection">
            <h3>Overall Product Totals</h3>
            {stats.overall.length === 0 && <p>No product totals match these filters.</p>}
            <div className="statsGrid">
              {stats.overall.map((row) => (
                <article className="statsCard" key={`${row.product_id}-${row.unit_name}`}>
                  <img className="productReceiptImage" src={row.product_picture || defaultProductPicture} alt="" />
                  <div>
                    <h4>{row.product_name}</h4>
                    {row.product_reference && <p>{row.product_reference}</p>}
                    <strong>{formatQuantity(row.total_quantity)} {row.unit_name}</strong>
                    <small>{row.receipt_count} receipts</small>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="statsSection">
            <h3>{user?.role === 'buyer' ? 'Totals By Farmer' : user?.role === 'farmer' ? 'Totals By Buyer' : 'Totals By Farmer And Buyer'}</h3>
            {stats.details.length === 0 && <p>No grouped totals match these filters.</p>}
            <div className="statsTableWrap">
              <table className="statsTable">
                <thead>
                  <tr>
                    <th>Product</th>
                    {user?.role !== 'farmer' && <th>Farmer</th>}
                    {user?.role !== 'buyer' && <th>Buyer</th>}
                    <th>Total</th>
                    <th>Receipts</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.details.map((row, index) => (
                    <tr key={`${row.product_id}-${row.unit_name}-${row.farmer_id || ''}-${row.buyer_id || row.buyer || ''}-${index}`}>
                      <td>
                        {row.product_name}
                        {row.product_reference && <small>{row.product_reference}</small>}
                      </td>
                      {user?.role !== 'farmer' && <td>{row.farmer_name}</td>}
                      {user?.role !== 'buyer' && <td>{row.buyer}</td>}
                      <td>{formatQuantity(row.total_quantity)} {row.unit_name}</td>
                      <td>{row.receipt_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
